import React from 'react';
import { Page } from '../../components/LayoutStructure';
import { ScrollToTop } from '@apps/common/components/ScrollToTop/ScrollToTop';
import { useBrandPageController } from '@apps/registry/common/components/Catalog/routes/BrandPage/BrandPage.controller';
import { StyledContainer, StyledDek, StyledDekMobile, StyledHeaderContainer, StyledHed, StyledPhotoContainer, StyledShowMoreButton, styles } from './BrandPage.styles';
import { Breadcrumbs, ProductGrid } from '@apps/registry/common/components/Catalog/components';
import { useTranslation } from '@shared/core';
import { SkeletonGroup, SkeletonText } from '@shared/components/Skeleton';
import { Seo } from '@shared/components/Seo';
import { SearchProductsProvider } from '../CatalogProductsIndex/components/SearchProductsProvider/SearchProductsProvider';

export interface BrandPageProps
  extends Readonly<{
    isMobile: boolean;
    brandLocator: string;
  }> {}

export const BrandPage = (props: BrandPageProps) => {
  return (
    <SearchProductsProvider>
      <Root {...props} />
    </SearchProductsProvider>
  );
};

const Root = (props: BrandPageProps) => {
  const { isMobile } = props;
  const {
    seoTrans,
    brandDetails,
    brandDetailsLoading,
    products,
    productsLoading,
    telemetryPageValue,
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    isFetchingMore,
    handleOnLoadMoreClick,
    hasNextPage,
    filters,
    isMobileDescriptionExpanded,
    setIsMobileDescriptionExpanded,
  } = useBrandPageController(props);

  const name = brandDetails?.name;
  const logo = brandDetails?.logoImage;
  const description = brandDetails?.description;
  const { t } = useTranslation('catalogRegistry');
  const { showMore, less } = t('brandPage');

  return (
    <Page>
      <ScrollToTop />
      <Seo
        title={seoTrans.shopBrandDetailsTitle({ brand: brandDetails?.name?.trim() || 'Brands' })}
        description={seoTrans.shopBrandDetailsDescription({ brand: brandDetails?.name?.trim() || 'Brands' })}
      />
      <StyledContainer __css={styles.container}>
        <SkeletonGroup
          isReady={!brandDetailsLoading}
          placeholder={
            <HeaderSkeleton
              isMobile={isMobile}
              description={description}
            />
          }
        >
          <Breadcrumbs
            defaultAlign="center"
            isMobile={isMobile}
            categoryId={brandDetails?.id}
            hideLastBreadcrumb={false}
            telemetryPageValue="brandPage"
          />
          <StyledHeaderContainer __css={styles.headerContainer}>
            {logo?.url ? (
              <StyledPhotoContainer __css={styles.logo}>
                <img
                  src={logo.url}
                  alt={logo.altTag ?? undefined}
                  height="100%"
                  width="100%"
                />
              </StyledPhotoContainer>
            ) : (
              <StyledHed
                as="h1"
                __css={styles.hed}
                padding={description ? null : ['0px 0px 20px 0px', null, '24px 0px 50px 0px']}
              >
                {name}
              </StyledHed>
            )}
            {description &&
              (isMobile ? (
                <>
                  <StyledDekMobile
                    as="p"
                    __css={styles.dek}
                    isDescriptionShown={isMobileDescriptionExpanded}
                  >
                    {description}
                  </StyledDekMobile>
                  <StyledShowMoreButton
                    as="button"
                    typographyVariant="hed1"
                    fontWeight="semibold"
                    onClick={() => setIsMobileDescriptionExpanded(!isMobileDescriptionExpanded)}
                  >
                    {isMobileDescriptionExpanded ? less() : showMore()}
                  </StyledShowMoreButton>
                </>
              ) : (
                <StyledDek
                  as="p"
                  __css={styles.dek}
                >
                  {description}
                </StyledDek>
              ))}
          </StyledHeaderContainer>
        </SkeletonGroup>
        <ProductGrid
          isMobile={isMobile}
          filters={filters}
          categoryLocator={null}
          categoryType="category"
          componentSource="productGrid"
          telemetryPageValue={telemetryPageValue}
          products={products}
          addCatalogItem={addCatalogItemToRegistry}
          addItemLoading={addCatalogItemLoading}
          isFetchingMore={isFetchingMore}
          onLoadMoreClick={handleOnLoadMoreClick}
          hasNextPage={hasNextPage}
          disableGridMarginBottom={false}
          showSkeleton={productsLoading}
          hideHeader={true}
          shouldOpenPdp={true}
        />
      </StyledContainer>
    </Page>
  );
};

const HeaderSkeleton = ({ isMobile, description }: { isMobile: boolean; description: Maybe<string> }) => (
  <StyledHeaderContainer __css={styles.headerContainer}>
    <StyledHed
      __css={styles.hed}
      padding={description ? null : ['0px 0px 20px 0px', null, '24px 0px 50px 0px']}
    >
      <SkeletonText
        rows={1}
        skeletonHeight={['32px', '45px', '56px']}
        width={['160px', '230px', '300px']}
      />
    </StyledHed>
    <StyledDek __css={styles.dek}>
      <SkeletonText
        rows={1}
        skeletonHeight={['60px', '110px', '150px']}
        width={['300px', '440px', '570px']}
      />
    </StyledDek>
    {isMobile ? (
      <SkeletonText
        rows={1}
        skeletonHeight="24px"
        width="86px"
      />
    ) : null}
  </StyledHeaderContainer>
);
